import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
  generateProgressInsights,
  analyzeSkillGaps,
  calculateLearningEfficiency,
  analyzeTrends,
  createProgressShare,
  setActiveView,
  setSelectedSkill,
  setSelectedTimeRange,
  toggleInsightExpansion,
  dismissInsight,
  applyInsightRecommendation,
  setInsightFilters,
  setTargetSkillLevel,
  focusOnSkillGap,
  applyOptimization,
  removeOptimization,
  updatePrivacySettings,
  deleteShare,
  updateShareAnalytics,
  clearError,
  refreshAllData,
  selectProgressInsights,
  selectFilteredInsights,
  selectSkillGaps,
  selectEfficiencyData,
  selectTrendData,
  selectShareHistory,
  selectPrivacySettings,
  selectActiveView,
  selectSelectedSkill,
  selectLoadingStates,
  selectInsightFilters,
  selectExpandedInsights,
} from '../store/slices/progressInsightsSlice';
import { selectMasteryStates } from '../store/slices/adaptiveProgressSlice';
import type { MasteryState } from '@coffeeplexity/shared';
import type {
  AIProgressInsight,
  InsightRecommendation,
} from '../components/ai/ProgressInsightsPanel';
import type {
  SkillGap,
  GapRecommendation,
  LearningStep,
} from '../components/ai/SkillGapAnalyzer';
import type {
  OptimizationSuggestion,
  LearningPattern,
} from '../components/ai/LearningEfficiencyTracker';
import type {
  ProgressPrediction,
  TrendAnomaly,
} from '../components/ai/ProgressTrendAnalyzer';
import { logger } from '../utils/logger';
import type {
  ProgressShareData,
  PrivacySettings,
} from '../components/ai/ProgressSharingPanel';

export interface UseProgressInsightsReturn {
  // Data
  insights: AIProgressInsight[];
  filteredInsights: AIProgressInsight[];
  skillGaps: SkillGap[];
  efficiencyData: any;
  trendData: any;
  shareHistory: ProgressShareData[];
  privacySettings: PrivacySettings;
  masteryStates: MasteryState[];

  // UI State
  activeView: string;
  selectedSkill: string | null;
  expandedInsights: string[];
  insightFilters: any;
  loadingStates: any;

  // Actions
  actions: {
    // View Management
    setActiveView: (view: any) => void;
    setSelectedSkill: (skillId: string | null) => void;
    setSelectedTimeRange: (range: '7d' | '30d' | '90d' | '1y') => void;

    // Insight Management
    toggleInsightExpansion: (insightId: string) => void;
    dismissInsight: (insightId: string) => void;
    applyInsightRecommendation: (
      insightId: string,
      recommendationId: string
    ) => void;
    setInsightFilters: (filters: any) => void;

    // Skill Gap Management
    setTargetSkillLevel: (skillId: string, level: number) => void;
    focusOnSkillGap: (skillId: string) => void;

    // Efficiency Management
    applyOptimization: (optimizationId: string) => void;
    removeOptimization: (optimizationId: string) => void;

    // Sharing Management
    createShare: (shareData: Partial<ProgressShareData>) => void;
    updatePrivacySettings: (settings: Partial<PrivacySettings>) => void;
    deleteShare: (shareId: string) => void;

    // Data Management
    refreshAllData: () => void;
    generateInsights: () => void;
    analyzeGaps: () => void;
    calculateEfficiency: () => void;
    analyzeTrends: () => void;

    // Error Handling
    clearError: () => void;
  };

  // Event Handlers
  handlers: {
    onInsightAction: (insightId: string, actionType: string) => void;
    onSkillFocus: (skillId: string) => void;
    onRecommendationSelect: (
      recommendation: InsightRecommendation | GapRecommendation
    ) => void;
    onOptimizationApply: (suggestion: OptimizationSuggestion) => void;
    onPatternExplore: (pattern: LearningPattern) => void;
    onPredictionExplore: (prediction: ProgressPrediction) => void;
    onAnomalyInvestigate: (anomaly: TrendAnomaly) => void;
    onShareProgress: (shareData: ProgressShareData) => void;
    onLearningPathStart: (skillId: string, path: LearningStep[]) => void;
  };
}

export function useProgressInsights(userId: string): UseProgressInsightsReturn {
  const dispatch = useAppDispatch();

  // Selectors
  const insights = useAppSelector(selectProgressInsights);
  const filteredInsights = useAppSelector(selectFilteredInsights);
  const skillGaps = useAppSelector(selectSkillGaps);
  const efficiencyData = useAppSelector(selectEfficiencyData);
  const trendData = useAppSelector(selectTrendData);
  const shareHistory = useAppSelector(selectShareHistory);
  const privacySettings = useAppSelector(selectPrivacySettings);
  const masteryStates = useAppSelector(selectMasteryStates);
  const activeView = useAppSelector(selectActiveView);
  const selectedSkill = useAppSelector(selectSelectedSkill);
  const expandedInsights = useAppSelector(selectExpandedInsights);
  const insightFilters = useAppSelector(selectInsightFilters);
  const loadingStates = useAppSelector(selectLoadingStates);

  // Actions
  const actions = {
    // View Management
    setActiveView: useCallback(
      (view: any) => {
        dispatch(setActiveView(view));
      },
      [dispatch]
    ),

    setSelectedSkill: useCallback(
      (skillId: string | null) => {
        dispatch(setSelectedSkill(skillId));
      },
      [dispatch]
    ),

    setSelectedTimeRange: useCallback(
      (range: '7d' | '30d' | '90d' | '1y') => {
        dispatch(setSelectedTimeRange(range));
      },
      [dispatch]
    ),

    // Insight Management
    toggleInsightExpansion: useCallback(
      (insightId: string) => {
        dispatch(toggleInsightExpansion(insightId));
      },
      [dispatch]
    ),

    dismissInsight: useCallback(
      (insightId: string) => {
        dispatch(dismissInsight(insightId));
      },
      [dispatch]
    ),

    applyInsightRecommendation: useCallback(
      (insightId: string, recommendationId: string) => {
        dispatch(applyInsightRecommendation({ insightId, recommendationId }));
      },
      [dispatch]
    ),

    setInsightFilters: useCallback(
      (filters: any) => {
        dispatch(setInsightFilters(filters));
      },
      [dispatch]
    ),

    // Skill Gap Management
    setTargetSkillLevel: useCallback(
      (skillId: string, level: number) => {
        dispatch(setTargetSkillLevel({ skillId, level }));
      },
      [dispatch]
    ),

    focusOnSkillGap: useCallback(
      (skillId: string) => {
        dispatch(focusOnSkillGap(skillId));
      },
      [dispatch]
    ),

    // Efficiency Management
    applyOptimization: useCallback(
      (optimizationId: string) => {
        dispatch(applyOptimization(optimizationId));
      },
      [dispatch]
    ),

    removeOptimization: useCallback(
      (optimizationId: string) => {
        dispatch(removeOptimization(optimizationId));
      },
      [dispatch]
    ),

    // Sharing Management
    createShare: useCallback(
      (shareData: Partial<ProgressShareData>) => {
        dispatch(createProgressShare(shareData));
      },
      [dispatch]
    ),

    updatePrivacySettings: useCallback(
      (settings: Partial<PrivacySettings>) => {
        dispatch(updatePrivacySettings(settings));
      },
      [dispatch]
    ),

    deleteShare: useCallback(
      (shareId: string) => {
        dispatch(deleteShare(shareId));
      },
      [dispatch]
    ),

    // Data Management
    refreshAllData: useCallback(() => {
      dispatch(refreshAllData());
    }, [dispatch]),

    generateInsights: useCallback(() => {
      dispatch(
        generateProgressInsights({
          userId,
          masteryStates,
          timeRange: '30d',
        })
      );
    }, [dispatch, userId, masteryStates]),

    analyzeGaps: useCallback(() => {
      dispatch(
        analyzeSkillGaps({
          userId,
          masteryStates,
          targetLevels: {},
        })
      );
    }, [dispatch, userId, masteryStates]),

    calculateEfficiency: useCallback(() => {
      dispatch(
        calculateLearningEfficiency({
          userId,
          timeRange: '30d',
        })
      );
    }, [dispatch, userId]),

    analyzeTrends: useCallback(() => {
      dispatch(
        analyzeTrends({
          userId,
          masteryStates,
          timeRange: '30d',
        })
      );
    }, [dispatch, userId, masteryStates]),

    // Error Handling
    clearError: useCallback(() => {
      dispatch(clearError());
    }, [dispatch]),
  };

  // Event Handlers
  const handlers = {
    onInsightAction: useCallback(
      (insightId: string, actionType: string) => {
        switch (actionType) {
          case 'dismiss':
            actions.dismissInsight(insightId);
            break;
          case 'apply':
            // Apply all recommendations for this insight
            const insight = insights.find(i => i.id === insightId);
            if (insight) {
              insight.recommendations.forEach(rec => {
                actions.applyInsightRecommendation(insightId, rec.id);
              });
            }
            break;
          default:
            logger.log('Unknown insight action:', actionType);
        }
      },
      [actions, insights]
    ),

    onSkillFocus: useCallback(
      (skillId: string) => {
        actions.setSelectedSkill(skillId);
        actions.setActiveView('gaps');
      },
      [actions]
    ),

    onRecommendationSelect: useCallback(
      (recommendation: InsightRecommendation | GapRecommendation) => {
        logger.log('Selected recommendation:', recommendation);
        // This would navigate to the recommended content or action
      },
      []
    ),

    onOptimizationApply: useCallback(
      (suggestion: OptimizationSuggestion) => {
        actions.applyOptimization(suggestion.id);
        logger.log('Applied optimization:', suggestion);
      },
      [actions]
    ),

    onPatternExplore: useCallback((pattern: LearningPattern) => {
      logger.log('Exploring pattern:', pattern);
      // This would show detailed pattern analysis
    }, []),

    onPredictionExplore: useCallback((prediction: ProgressPrediction) => {
      logger.log('Exploring prediction:', prediction);
      // This would show detailed prediction analysis
    }, []),

    onAnomalyInvestigate: useCallback((anomaly: TrendAnomaly) => {
      logger.log('Investigating anomaly:', anomaly);
      // This would show detailed anomaly analysis
    }, []),

    onShareProgress: useCallback((shareData: ProgressShareData) => {
      logger.log('Shared progress:', shareData);
      // This would handle the actual sharing (social media, etc.)
    }, []),

    onLearningPathStart: useCallback(
      (skillId: string, path: LearningStep[]) => {
        logger.log('Starting learning path for', skillId, path);
        // This would navigate to the learning path or create a journey
      },
      []
    ),
  };

  // Auto-load data on mount
  useEffect(() => {
    if (userId && masteryStates.length > 0) {
      actions.generateInsights();
      actions.analyzeGaps();
      actions.calculateEfficiency();
      actions.analyzeTrends();
    }
  }, [userId, masteryStates.length]); // Only depend on length to avoid infinite loops

  return {
    // Data
    insights,
    filteredInsights,
    skillGaps,
    efficiencyData,
    trendData,
    shareHistory,
    privacySettings,
    masteryStates,

    // UI State
    activeView,
    selectedSkill,
    expandedInsights,
    insightFilters,
    loadingStates,

    // Actions
    actions,

    // Event Handlers
    handlers,
  };
}
